import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { db, storage } from './firebase';


// Helper to find franchise by location
const findFranchiseForLocation = async (taluka, district, state) => {
  try {
    const q = query(
      collection(db, 'franchises'),
      where('status', '==', 'active')
    );

    const snapshot = await getDocs(q);

    for (const doc of snapshot.docs) {
      const franchise = doc.data();
      if (franchise.assignedAreas && Array.isArray(franchise.assignedAreas)) {
        const hasAccess = franchise.assignedAreas.some(area =>
          area.state === state &&
          area.district === district &&
          area.taluka === taluka
        );

        if (hasAccess) {
          console.log(`Found franchise ${doc.id} for location: ${taluka}, ${district}`);
          return doc.id;
        }
      }
    }
    return null;
  } catch (error) {
    console.error('Error searching for franchise:', error);
    return null;
  }
};

// Create or update company profile
export const createCompanyProfile = async (companyData, userId) => {
  try {
    // Use userId as companyId to ensure one company per user
    const companyId = userId;

    // Fetch user data to check for franchiseId
    const userDocRef = await getDoc(doc(db, 'users', userId));
    let franchiseId = null;

    // Check location based assignment first
    if (companyData.taluka && companyData.district && companyData.state) {
      const foundFranchiseId = await findFranchiseForLocation(
        companyData.taluka,
        companyData.district,
        companyData.state
      );
      if (foundFranchiseId) {
        franchiseId = foundFranchiseId;
      }
    }

    // Fallback to user data if not found by location
    if (!franchiseId && userDocRef.exists()) {
      const userData = userDocRef.data();
      // Check for franchiseId directly or via referredBy if it's a franchise
      if (userData.franchiseId) {
        franchiseId = userData.franchiseId;
      } else if (userData.referredBy) {
        // Double check if referredBy is a franchise (optional but safer)
        const franchiseDoc = await getDoc(doc(db, 'franchises', userData.referredBy));
        if (franchiseDoc.exists()) {
          franchiseId = userData.referredBy;
        }
      }
    }

    const companyDoc = {
      ...companyData,
      id: companyId,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      profileCompletion: calculateProfileCompletion(companyData)
    };

    // Add franchiseId if found
    if (franchiseId) {
      companyDoc.franchiseId = franchiseId;
      console.log('Linking company to franchise:', franchiseId);

      // Also update the user document
      try {
        const userRef = doc(db, 'users', userId);
        // User requested to save franchiseId under 'referredBy' field in users collection
        await setDoc(userRef, { referredBy: franchiseId }, { merge: true });
        console.log('Updated user document with referredBy:', franchiseId);
      } catch (err) {
        console.error('Error updating user document with referredBy:', err);
      }
    }

    await setDoc(doc(db, 'companies', companyId), companyDoc);

    // Update user's companyIds to include this company
    await updateUserCompanyIds(userId, companyId);

    return { success: true, companyId };
  } catch (error) {
    console.error('Create company profile error:', error);
    return { success: false, error: error.message };
  }
};

// Get company profile
export const getCompanyProfile = async (companyId) => {
  try {
    const companyDoc = await getDoc(doc(db, 'companies', companyId));
    if (companyDoc.exists()) {
      return { success: true, data: companyDoc.data() };
    } else {
      return { success: false, error: 'Company not found' };
    }
  } catch (error) {
    console.error('Get company profile error:', error);
    return { success: false, error: error.message };
  }
};

// Get company profile by user ID
export const getCompanyProfileByUserId = async (userId) => {
  try {
    const companyDoc = await getDoc(doc(db, 'companies', userId));
    if (companyDoc.exists()) {
      return { success: true, data: companyDoc.data() };
    } else {
      return { success: false, error: 'Company not found' };
    }
  } catch (error) {
    console.error('Get company profile by user ID error:', error);
    return { success: false, error: error.message };
  }
};

// Update company profile
export const updateCompanyProfile = async (companyId, companyData) => {
  try {
    const updatedData = {
      ...companyData,
      updatedAt: serverTimestamp(),
      profileCompletion: calculateProfileCompletion(companyData)
    };

    // Check if location changed and update franchiseId
    if (companyData.taluka && companyData.district && companyData.state) {
      const foundFranchiseId = await findFranchiseForLocation(
        companyData.taluka,
        companyData.district,
        companyData.state
      );
      if (foundFranchiseId) {
        updatedData.franchiseId = foundFranchiseId;
        console.log('Updating company franchise based on location:', foundFranchiseId);

        // Also update the user document
        try {
          // companyId is the userId for companies
          const userRef = doc(db, 'users', companyId);
          // User requested to save franchiseId under 'referredBy' field in users collection
          await setDoc(userRef, { referredBy: foundFranchiseId }, { merge: true });
          console.log('Updated user document with referredBy:', foundFranchiseId);
        } catch (err) {
          console.error('Error updating user document with referredBy:', err);
        }
      }
    }

    await updateDoc(doc(db, 'companies', companyId), updatedData);
    return { success: true };
  } catch (error) {
    console.error('Update company profile error:', error);
    return { success: false, error: error.message };
  }
};

// Upload company logo
export const uploadCompanyLogo = async (file, companyId) => {
  try {
    // Compress image
    const compressedFile = await compressImage(file, 800, 600, 0.8);

    const fileName = `logo_${Date.now()}.${compressedFile.name.split('.').pop()}`;
    const storageRef = ref(storage, `companies/${companyId}/logo/${fileName}`);

    // Add metadata for better organization
    const metadata = {
      contentType: compressedFile.type,
      customMetadata: {
        uploadedBy: 'company-profile',
        companyId: companyId
      }
    };

    await uploadBytes(storageRef, compressedFile, metadata);
    const downloadURL = await getDownloadURL(storageRef);

    return { success: true, url: downloadURL };
  } catch (error) {
    console.error('Upload company logo error:', error);

    // Handle specific Firebase Storage errors
    if (error.code === 'storage/unauthorized') {
      return { success: false, error: 'You do not have permission to upload files. Please check your authentication.' };
    } else if (error.code === 'storage/canceled') {
      return { success: false, error: 'Upload was canceled.' };
    } else if (error.code === 'storage/unknown') {
      return { success: false, error: 'An unknown error occurred during upload.' };
    }

    return { success: false, error: error.message };
  }
};

// Upload company images
export const uploadCompanyImages = async (files, companyId) => {
  try {
    const uploadPromises = files.map(async (file, index) => {
      const compressedFile = await compressImage(file, 1200, 800, 0.7);
      const fileName = `image_${index}_${Date.now()}.${compressedFile.name.split('.').pop()}`;
      const storageRef = ref(storage, `companies/${companyId}/images/${fileName}`);

      // Add metadata for better organization
      const metadata = {
        contentType: compressedFile.type,
        customMetadata: {
          uploadedBy: 'company-profile',
          companyId: companyId,
          imageIndex: index.toString()
        }
      };

      await uploadBytes(storageRef, compressedFile, metadata);
      return await getDownloadURL(storageRef);
    });

    const urls = await Promise.all(uploadPromises);
    return { success: true, urls };
  } catch (error) {
    console.error('Upload company images error:', error);

    // Handle specific Firebase Storage errors
    if (error.code === 'storage/unauthorized') {
      return { success: false, error: 'You do not have permission to upload files. Please check your authentication.' };
    } else if (error.code === 'storage/canceled') {
      return { success: false, error: 'Upload was canceled.' };
    } else if (error.code === 'storage/unknown') {
      return { success: false, error: 'An unknown error occurred during upload.' };
    }

    return { success: false, error: error.message };
  }
};

// Add recruiter to company
export const addRecruiter = async (companyId, recruiterData) => {
  try {
    const recruiterId = `recruiter_${Date.now()}`;
    const recruiterDoc = {
      ...recruiterData,
      companyId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(doc(db, 'companies', companyId, 'recruiters', recruiterId), recruiterDoc);
    return { success: true, recruiterId };
  } catch (error) {
    console.error('Add recruiter error:', error);
    return { success: false, error: error.message };
  }
};

// Get company recruiters
export const getCompanyRecruiters = async (companyId) => {
  try {
    const companyDoc = await getDoc(doc(db, 'companies', companyId));

    if (companyDoc.exists()) {
      const companyData = companyDoc.data();
      const recruiters = companyData.recruiters || [];
      return { success: true, data: recruiters };
    } else {
      return { success: false, error: 'Company not found' };
    }
  } catch (error) {
    console.error('Get company recruiters error:', error);
    return { success: false, error: error.message };
  }
};

// Get all companies (for job seekers)
export const getAllCompanies = async (limitCount = 20) => {
  try {
    const companiesRef = collection(db, 'companies');
    const snapshot = await getDocs(companiesRef);

    const companies = [];
    snapshot.forEach(doc => {
      companies.push({ id: doc.id, ...doc.data() });
    });

    // Sort in memory by createdAt (newest first)
    companies.sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || 0;
      const bTime = b.createdAt?.toMillis?.() || 0;
      return bTime - aTime;
    });

    // Apply limit after sorting
    const limitedCompanies = companies.slice(0, limitCount);

    return { success: true, data: limitedCompanies };
  } catch (error) {
    console.error('Get all companies error:', error);
    return { success: false, error: error.message };
  }
};

// Search companies
export const searchCompanies = async (searchTerm) => {
  try {
    const companiesRef = collection(db, 'companies');
    const q = query(
      companiesRef,
      where('companyName', '>=', searchTerm),
      where('companyName', '<=', searchTerm + '\uf8ff')
    );
    const snapshot = await getDocs(q);

    const companies = [];
    snapshot.forEach(doc => {
      companies.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, data: companies };
  } catch (error) {
    console.error('Search companies error:', error);
    return { success: false, error: error.message };
  }
};

// Delete company
export const deleteCompany = async (companyId) => {
  try {
    // Delete company document from Firestore
    await deleteDoc(doc(db, 'companies', companyId));

    return { success: true };
  } catch (error) {
    console.error('Delete company error:', error);
    return { success: false, error: error.message };
  }
};

// Helper function to calculate profile completion
const calculateProfileCompletion = (companyData) => {
  // Only count visible required fields (matching CompanyProfileForm)
  const requiredFields = [
    'companyName',
    'industry',
    'companySize',
    'companyType',
    'location',
    'description',
    'email',
    'logoUrl'
  ];

  const filledFields = requiredFields.filter(field =>
    companyData[field] &&
    companyData[field] !== '' &&
    companyData[field] !== null &&
    companyData[field] !== undefined
  );

  return Math.round((filledFields.length / requiredFields.length) * 100);
};

// Helper function to update user's company IDs
const updateUserCompanyIds = async (userId, companyId) => {
  try {
    const { doc, getDoc, updateDoc } = await import('firebase/firestore');
    const userDoc = await getDoc(doc(db, 'users', userId));

    if (userDoc.exists()) {
      const userData = userDoc.data();
      const companyIds = userData.companyIds || [];

      if (!companyIds.includes(companyId)) {
        companyIds.push(companyId);
        await updateDoc(doc(db, 'users', userId), { companyIds });
      }
    }
  } catch (error) {
    console.error('Update user company IDs error:', error);
  }
};

// Image compression helper
const compressImage = (file, maxWidth, maxHeight, quality) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;

      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          const compressedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now()
          });
          resolve(compressedFile);
        },
        file.type,
        quality
      );
    };

    img.src = URL.createObjectURL(file);
  });
};
